import { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../apis/auth/AuthProvider";
import logo from "../../assets/GiantLog-logo.png";
import { getErrorMessage, getUiInterface } from "../../locales/translationUtils";
import AlertOverlay from "../../components/Alert";

const Login: React.FC = () => {
  const { loginAction, validateSession } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      try {
        const storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
        if (!storedUser) {
          setLoading(false);
          return;
        }

        const isValid = await validateSession();
        if (isValid) {
          navigate("/");
        }
      } catch (error) {
        console.error("Sessão inválida, exigindo novo login.");
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const loginObj = {
      username: formData.username,
      password: formData.password,
      id: 0,
      rememberMe: formData.rememberMe,
    };

    try {
      await loginAction(loginObj);

      navigate("/");
    } catch (err: any) {
      let errorMessages: string[] | null = err?.data?.messages;

      if (!errorMessages && err?.message) {
        errorMessages = [err.message];
      }

      setErrorMessage(
        err?.status === 401
          ? getErrorMessage("loginFailedError")
          : `${getErrorMessage("genericError")}: ${errorMessages?.join("; ")}.`
      );

      setShowError(true);
    }
  };

  if (loading) return null;

  return (
    <>
      {showError && errorMessage && (
        <AlertOverlay
          message={errorMessage}
          variant="danger"
          timeInSeconds={5}
          onClose={() => setShowError(false)}
        />
      )}

      <Modal
        show={true}
        backdrop="static"
        keyboard={false}
        centered
        onEntered={() => {
          if (usernameRef.current) {
            usernameRef.current.focus();
          }
        }}
      >
        <Modal.Header className="d-flex justify-content-center align-items-center">
          <img src={logo} alt="Logo" style={{ width: "150px", height: "auto" }} />
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formLogin">
              <Form.Label>{getUiInterface("username")}:</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                ref={usernameRef}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="formPassword" className="mt-3">
              <Form.Label>{getUiInterface("password")}:</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && formData.username.trim()) {
                    handleLogin(e);
                  }
                }}
              />
            </Form.Group>
            <Form.Group controlId="formRememberMe" className="mt-3">
              <Form.Check
                type="checkbox"
                label={getUiInterface("rememberMe")}
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleLogin}>
            {getUiInterface("login")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Login;
